import { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import {
  DiscountItemInfo,
  PromoLog,
  PromoRequest,
  PromoRequestFilters,
  PromoResponseJSON,
  getPromoCodeRequests,
} from '~/services/PromoService';

import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';

type UsePromoRequestsProps = {
  initPage?: number;
  initLimit?: number;
  initCodeName?: string;
  initUserId?: string;
  initErrorCode?: string;
  brand: string;
};

export type InputPromoOrder = PromoRequest['bodyJSON']['order'] | PromoLog['details']['logResults']['inputOrder'];

export type PromoRequestItem = definitions['Discount Request Item'] & {
  codeName?: string;
  region?: string;
  deviceType?: string;
  requestSource?: string;
  requestAt?: string;
  currencyCode?: string;
  uniqueKey?: string;
  userId?: string;
  isDeleted?: boolean;
  statusCode?: number;
  errorCode?: string;
  errorMsg?: string;
  clientOrderVersion?: number;
  fullRequest: InputPromoOrder;
  discountItem?: DiscountItemInfo | null;
  mx?: string;
};

export type PromoRequestLoyaltyItemExtended = definitions['LoyaltyCalculationRequestItem'] & {
  codeName?: string;
  region?: string;
  deviceType?: string;
  orderId?: string;
  promoKey?: string;
  uniqueKey?: string;
  loyaltyBonusFilterMethod?: string;
  resultCode?: string;
};

export default function usePromoRequests({
  initPage,
  initLimit,
  initCodeName,
  initErrorCode,
  initUserId,
  brand,
}: UsePromoRequestsProps): {
  promoRequests: Array<PromoRequest>;
  promoRequestItems: Array<PromoRequestItem>;
  promoRequestLoyaltyItems: Array<PromoRequestLoyaltyItemExtended>;
  totalRows: number;
  isLoading: boolean;
  promoRequestsFilter: PromoRequestFilters;
  setPromoRequestsFilter: (filter: PromoRequestFilters) => void;
  fetchPromoRequestData: () => Promise<void>;
} {
  const { enqueueSnackbar } = useSnackbar();
  const [promoRequests, setPromoRequests] = useState<Array<PromoRequest>>([]);
  const [promoRequestItems, setPromoRequestItems] = useState<Array<PromoRequestItem>>([]);
  const [promoRequestLoyaltyItems, setPromoRequestLoyaltyItems] = useState<Array<PromoRequestLoyaltyItemExtended>>([]);
  const [promoRequestsFilter, setPromoRequestsFilter] = useState<PromoRequestFilters>({
    codeName: initCodeName,
    userId: initUserId,
    brand: brand as definitions['Promo Brands'],
    errorCode: initErrorCode,
    page: initPage ?? 0,
    limit: initLimit > 0 ? initLimit : 10,
  });
  const [totalRows, setTotalRows] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchPromoRequestData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getPromoCodeRequests(promoRequestsFilter);
      if (response.status === 200) {
        const newPromoRequests = response.result.promoRequests.map((pr, i) => ({
          uniqueKey: `${pr.codeName}-${pr.createdAt}-${i}`,
          ...pr,
        }));

        const newPromoRequestItems = response.result.promoRequests.flatMap((pr, requestIndex) => {
          const codeName = pr?.codeName;
          if (typeof pr?.bodyJSON == 'string') {
            try {
              pr.bodyJSON = JSON.parse(pr?.bodyJSON);
            } catch (e) {
              console.error(
                `(newPromoRequestItems) nonbreaking bodyJSON parse issue. JSON.parse error ${pr.codeName}`,
                e,
              );
            }
          }
          const region = pr?.bodyJSON?.order?.region;
          const currencyCode = getCurrencyCodeByRegionCode(region);

          return (
            pr?.bodyJSON?.order?.items.map(
              (item, itemIndex): PromoRequestItem => ({
                ...item,
                codeName: codeName,
                region,
                deviceType: pr?.bodyJSON?.order?.deviceType,
                userId: pr?.bodyJSON?.userId,
                requestAt: pr?.createdAt,
                currencyCode: currencyCode,
                uniqueKey: `${pr.codeName}-${pr.createdAt}-${requestIndex}-${itemIndex}`,
                requestSource: pr?.bodyJSON?.requestSource,
                statusCode: pr?.statusCode,
                errorCode: pr?.errorCode,
                errorMsg: pr?.errorMsg,
                fullRequest: pr?.bodyJSON?.order,
              }),
            ) ?? []
          );
        });

        const newPromoRequestLoyaltyItems = response.result.promoRequests.flatMap((pr, requestIndex) => {
          // Return empty array if no promo request or no order
          if (!pr) {
            return [];
          }

          // Try to get loyaltyItems, but continue even if missing
          const loyaltyItems = pr?.bodyJSON?.order?.loyaltyItems;
          if (!loyaltyItems || !Array.isArray(loyaltyItems)) {
            return [];
          }

          return loyaltyItems
            .map((item, itemIndex): PromoRequestLoyaltyItemExtended | null => {
              // Return null only if item is completely missing
              if (item == null) {
                return null;
              }

              // Try to get response data, but continue even if missing
              const response: PromoResponseJSON | undefined = pr?.responseJSON?.result as unknown as PromoResponseJSON;
              const loyaltyPointsBonusResultV1 = response?.promo?.loyaltyPointsBonusResultV1;
              const resultItem = loyaltyPointsBonusResultV1?.loyaltyCalculationResponseItems?.find(
                (i) => i?.itemId === item?.itemId,
              );

              // Return item even if some fields are missing - use defaults/fallbacks
              return {
                ...item,
                codeName: pr?.codeName ?? undefined,
                region: pr?.bodyJSON?.order?.region ?? undefined,
                deviceType: pr?.bodyJSON?.order?.deviceType ?? undefined,
                loyaltyProductType: item?.loyaltyProductType ?? undefined,
                promoKey: resultItem?.promoKey ?? undefined,
                loyaltyBonusFilterMethod: loyaltyPointsBonusResultV1?.loyaltyBonusFilterMethod ?? undefined,
                resultCode: loyaltyPointsBonusResultV1?.resultCode ?? 'NO-ITEM-FOUND-IN-RESULT',
                price: item?.price ?? undefined,
                itemId: item?.itemId ?? undefined,
                offerId: item?.offerId ?? undefined,
                packageId: item?.packageId ?? undefined,
                uniqueKey: `${pr?.codeName ?? 'unknown'}-${pr?.createdAt ?? Date.now()}-${requestIndex}-${itemIndex}`,
              };
            })
            .filter((item): item is PromoRequestLoyaltyItemExtended => item != null);
        });

        setPromoRequests(newPromoRequests);
        setPromoRequestItems(newPromoRequestItems);
        setPromoRequestLoyaltyItems(newPromoRequestLoyaltyItems);
        setTotalRows(Number(response.total));
      } else {
        const err = JSON.stringify(response) || 'Failed to fetch promo requests';
        enqueueSnackbar(err, { variant: 'error' });
        throw new Error(err);
      }
    } catch (err) {
      console.error('fetchPromoRequestData error', err);
      if ('errors' in err && err.errors?.length > 0) {
        err.errors.forEach((e) => {
          if ('message' in e) {
            enqueueSnackbar(`Promo Request Error: ${e.path} "${e.value}" ${e.message}`, {
              variant: 'error',
              autoHideDuration: 5000,
            });
          } else {
            enqueueSnackbar(`Promo Request Error: ${JSON.stringify(e)}`, {
              variant: 'error',
              autoHideDuration: 5000,
            });
          }
        });
      } else {
        enqueueSnackbar(`Promo Request (empty): ${JSON.stringify(err)}`, { variant: 'error', autoHideDuration: 5000 });
      }
      // Reset states on error to maintain consistency
      setPromoRequests([]);
      setPromoRequestItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [promoRequestsFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchPromoRequestData();
    // Cleanup function to reset states when component unmounts
    return () => {
      setPromoRequests([]);
      setPromoRequestItems([]);
    };
  }, [promoRequestsFilter, fetchPromoRequestData]);

  return {
    promoRequests,
    promoRequestItems,
    promoRequestLoyaltyItems,
    totalRows,
    isLoading,
    promoRequestsFilter,
    setPromoRequestsFilter,
    fetchPromoRequestData,
  };
}
